import * as React from "react";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import * as _ from "lodash";
import { useMemo } from "react";

import { PaginatedParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Modal } from "src/library/components/Modal";
import { Table } from "src/core/dashboard/components/widgets";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { ImportModal } from "src/core/shared/components/ImportModal";
import { PriceExportModal } from "src/core/prices/components/PriceExportModal";
import { FilterParams } from "src/core/shared/api";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import {
  IPriceCode,
  IPriceList
} from "src/core/shared/api/price-change.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import CreateUpdatePriceListModal from "src/core/prices/components/CreateUpdatePriceListModal";
import {
  PricesRequestName,
  usePricesApi
} from "src/core/prices/api/prices.api";
import { ERPSeedless } from "src/utils/constants";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { getPriceListColumns } from "src/core/shared/components/tables/priceListColumns";

const defaultPagination: PaginatedParams<IPriceCode> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

/**
 * Main "Price Lists" View
 *
 * @returns
 */
const PriceListsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history } = props;
  const [ showImportModal, setShowImportModal ] = React.useState(false);
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const [ showCreatePriceListModal, setShowCreatePriceListModal ] = React.useState(false);
  const [ priceListPaginatedResponse, getPriceListPaginated ] = usePricesApi(PricesRequestName.LIST_PRICE_LISTS);
  const [ deletePriceListResponse, deletePriceList ] = usePricesApi(PricesRequestName.DELETE_PRICE_LIST);
  const [ triggerFtpImportResponse, triggerFtpImportRequest ] = useImportSettingApi("TRIGGER_FTP_IMPORT");
  const [ deletePriceListData, setDeletePriceListData ] = React.useState<Pick<IPriceList, "id" | "name" | "code"> | null>(null);
  const [ updatePriceListData, setUpdatePriceListData ] = React.useState<Pick<IPriceList, "id" | "name" | "code"> | null>(null);
  const checkPermission = useCheckUserPermission();
  const canCreatePriceList = checkPermission("create-price-code");
  const canAmendedPriceList = checkPermission("amend-price-code");
  const canDeletePriceList = checkPermission("delete-price-code");

  const {
    erp, installedModules, importSettings
  } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    installedModules: global.settings ? global.settings.installedModules : [],
    importSettings: global.settings?.importSettings
  }));

  const onFetchData = React.useCallback((params: {filters: FilterParams<IPriceList>, pagination: PaginatedParams<IPriceCode>}) => {
    getPriceListPaginated({
      pagination: params.pagination,
      filters: params.filters
    });
  }, [ getPriceListPaginated ]);

  React.useEffect(() => {
    if (triggerFtpImportResponse.data) {
      toast.success(triggerFtpImportResponse.data.message);
    } else if (triggerFtpImportResponse.error) {
      toast.error(triggerFtpImportResponse.error.message);
    }
  }, [ triggerFtpImportResponse.data, triggerFtpImportResponse.error ]);

  React.useEffect(() => {
    if (deletePriceListResponse.data) {
      toast.success("Your price list has been successfully deleted.");
    }

    if (deletePriceListResponse.error) {
      toast.error(`Your price list could not be deleted - ${deletePriceListResponse.error.message}`);
    }
  }, [ deletePriceListResponse ]);

  const actionButtons = useMemo(() => {
    const likeToDoButtons = [
      <Button
        key="export-products"
        onClick={() => setShowExportModal(true)}
      >
        Export Price code
      </Button>
    ];

    const hasPriceCSVImportSetting = _.find(importSettings, is => is.type === ImportSettingType.Price && is.origin === ImportOrigin.CSV && !is.options?.exportOnly);

    if (canCreatePriceList && hasPriceCSVImportSetting) {
      likeToDoButtons.unshift(
        <Button
          key="import-products"
          onClick={() => setShowImportModal(true)}
        >
          Import Prices
        </Button>
      );
    }

    if (canCreatePriceList && erp === ERPSeedless) {
      likeToDoButtons.push(
        <Button
          key="export-products"
          onClick={() => setShowCreatePriceListModal(true)}
        >
          Create Price list
        </Button>
      );
    }

    const hasPriceSFTPImportSetting = _.find(importSettings, is => is.type === ImportSettingType.Price && is.origin === ImportOrigin.SFTP);

    if (hasPriceSFTPImportSetting && canCreatePriceList) {
      likeToDoButtons.push((
        <Button
          key="create-product"
          onClick={() => triggerFtpImportRequest(hasPriceSFTPImportSetting.id)}
        >
          Sync prices from FTP
        </Button>
      ));
    }

    return likeToDoButtons;
  }, [
    canCreatePriceList,
    erp,
    importSettings,
    triggerFtpImportRequest
  ]);

  const columns = React.useMemo(() => {
    return getPriceListColumns({
      actionButton: info => {
        const items = [];

        if (installedModules.includes("price-change")) {
          items.push({
            title: "View Prices",
            action: () => {
              history.push(`/admin/products/price-lists/${info.row.original.id}/changes`);
            }
          });
        } else {
          items.push({
            title: "View/Edit",
            action: () => {
              history.push(`/admin/products/price-lists/${info.row.original.id}`);
            }
          });
        }

        if (canAmendedPriceList && info.row.original.code !== "DEFAULT" && erp === "seedless") {
          items.push({
            title: "Edit",
            action: () => {
              setUpdatePriceListData({
                id: info.row.original.id,
                name: info.row.original.name,
                code: info.row.original.code
              });
            }
          });
        }

        if (canDeletePriceList && info.row.original.code !== "DEFAULT" && erp === "seedless") {
          items.push({
            title: "Delete",
            action: () => {
              setDeletePriceListData({
                id: info.row.original.id,
                name: info.row.original.name,
                code: info.row.original.code
              });
            }
          });
        }

        return (
          <ActionButton
            title="Actions"
            items={items}
          />
        );
      }
    });
  }, [
    canAmendedPriceList,
    canDeletePriceList,
    erp,
    history,
    installedModules
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        Header={(
          <LikeToDo
            copy="What would you like to do?"
            buttons={actionButtons}
          />
        )}
      >

        <Table
          tableOf="Price Lists"
          data={priceListPaginatedResponse.data?.result ? priceListPaginatedResponse.data.result : []}
          columns={columns}
          onFetchData={onFetchData}
          loading={priceListPaginatedResponse.pending}
          defaultPagination={defaultPagination}
          defaultVisibility={{ id: false }}
          rowCount={priceListPaginatedResponse.data?.count}
          pageCount={priceListPaginatedResponse.data?.pages}
        />
      </MainContentWrapper>

      <ImportModal
        open={showImportModal}
        onDismiss={() => setShowImportModal(false)}
        type="price"
        terminology={props.terminology}
      />

      <PriceExportModal
        open={showExportModal}
        onDismiss={() => setShowExportModal(false)}
      />

      <CreateUpdatePriceListModal
        open={showCreatePriceListModal || !!updatePriceListData}
        modalData={updatePriceListData}
        onDismiss={() => {
          setUpdatePriceListData(null);
          setShowCreatePriceListModal(false);
        }}
      />

      <Modal
        small
        show={deletePriceListData !== null}
        type="confirm"
        title="Are you sure?"
        body="Please confirm that you wish to permanently delete this price list."
        actions={[
          {
            name: "No, cancel",
            $buttonType: "secondary",
            action: () => setDeletePriceListData(null)
          },
          {
            name: "I confirm",
            $buttonType: "primary",
            action: () => {
              if (deletePriceListData) {
                setDeletePriceListData(null);
                deletePriceList({ id: deletePriceListData.id });
              }
            }
          }
        ]}
        backgroundAction={() => setDeletePriceListData(null)}
      />
    </Layout>
  );
};

export default PriceListsPage;
